import { motion } from "motion/react";
import type { BaseSyntheticEvent } from "react";
import { useCallback, useState } from "react";

import { useRoutedDialogs } from "@/components/app/dialogs/dialog-provider";
import { useLocalEntryDialog } from "@/components/app/dialogs/use-local-entry-dialog";
import ImageViewerDialogContent, {
  type ImageViewerDialogContentProps,
} from "@/components/shared/image-viewer-dialog-content";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { usePostContext } from "@/contexts/post-context";
import { useMobile } from "@/hooks/use-mobile";
import { DIALOG } from "@/utils/dialog-routes";
import type { PostMediaMeta } from "@/utils/types";

import { PostImageCarousel } from "./post-image-carousel";
interface PostImageSectionProps {
  urls: string[];
  postHash?: string;
  optimize?: boolean;
  mediaMeta?: PostMediaMeta;
  sourceLink?: string;
}
export const MAX_IMAGE_HEIGHT = 800;
export const MAX_IMAGE_WIDTH = 900;
export const PostImageSection = ({
  urls,
  postHash,
  mediaMeta = {},
  optimize = true,
  sourceLink,
}: PostImageSectionProps) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const { hasMouse } = useMobile();
  const { setDialog } = useRoutedDialogs();
  const {
    open: localEntryOpen,
    props: localEntryProps,
    setOpen: setLocalEntryOpen,
  } = useLocalEntryDialog(DIALOG.ImageViewer, {
    props: {
      index: String(selectedIndex),
      mediaMeta,
      optimize,
      sourceLink,
      urls,
    },
  });
  const activeLocalEntryProps =
    (localEntryProps as ImageViewerDialogContentProps | undefined) ?? undefined;
  let resolvedPostHash = postHash;

  if (!resolvedPostHash) {
    // This condition is fine, it won't change during the component's lifecycle
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { post, repost, isPureRepost } = usePostContext() || {};
    resolvedPostHash = isPureRepost ? repost?.postHash : post?.postHash;
  }

  const handleSelect = useCallback((index: number) => {
    setSelectedIndex(index);
  }, []);

  if (urls.length === 0) return null;

  const preview = (onOpen?: () => void) => (
    <div
      className="
        group relative m-0 size-full max-w-fit cursor-pointer overflow-hidden
      "
      onClick={onOpen}
      onKeyDown={(event) => {
        if (!onOpen) {
          return;
        }

        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <PostImageCarousel
        urls={urls}
        postHash={resolvedPostHash}
        maxWidth={MAX_IMAGE_WIDTH}
        maxHeight={MAX_IMAGE_HEIGHT}
        onSelect={handleSelect}
        optimize={optimize}
        mediaMeta={mediaMeta}
        sourceLink={sourceLink}
      />
    </div>
  );

  if (resolvedPostHash) {
    return (
      <div className="contents">
        {preview(() => {
          setDialog(DIALOG.ImageViewer, {
            open: true,
            params: {
              index: String(selectedIndex),
              postHash: resolvedPostHash,
              ...(sourceLink ? { sourceLink } : {}),
            },
          });
        })}
      </div>
    );
  }

  return (
    <div>
      <Dialog open={localEntryOpen} onOpenChange={setLocalEntryOpen}>
        <DialogTrigger render={preview()} />
        <DialogContent
          className="
            h-screen max-h-screen w-screen max-w-[100vw] rounded-none border-0
            bg-black/20 object-contain
          "
          initialFocus={false}
        >
          <DialogTitle className="hidden">Post Image</DialogTitle>
          <DialogDescription className="hidden">Post Image</DialogDescription>
          <motion.div
            className="flex size-full items-center justify-center"
            drag={hasMouse ? false : "y"}
            dragConstraints={hasMouse ? undefined : { top: 0, bottom: 0 }}
            onDragEnd={(
              _: BaseSyntheticEvent,
              info: {
                point: {
                  y: number;
                };
              },
            ) => {
              if (!hasMouse && info.point.y > 100) {
                setLocalEntryOpen(false);
              }
            }}
          >
            <ImageViewerDialogContent
              index={activeLocalEntryProps?.index ?? String(selectedIndex)}
              mediaMeta={activeLocalEntryProps?.mediaMeta ?? mediaMeta}
              optimize={activeLocalEntryProps?.optimize ?? optimize}
              setOpen={setLocalEntryOpen}
              sourceLink={activeLocalEntryProps?.sourceLink ?? sourceLink}
              urls={activeLocalEntryProps?.urls ?? urls}
            />
          </motion.div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
